type UsageStatus = {
  enabled: boolean;
  perClientLimit: number;
  globalLimit: number;
  globalUsed: number;
  globalRemaining: number;
  resetHourJst: number;
};

type UsageResetResult = {
  ok: true;
  previousGlobalUsed: number;
  resetAt: string;
};

type AiModelStatus = {
  visionModel?: string;
  narrativeModel?: string;
};

let aiModelsPromise: Promise<AiModelStatus> | null = null;

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

async function loadUsageStatus(): Promise<UsageStatus> {
  const response = await fetch("/admin/api/usage-status", { headers: { accept: "application/json" }, cache: "no-store" });
  if (!response.ok) throw new Error("利用制限の状態を取得できませんでした。");
  return (await response.json()) as UsageStatus;
}

async function loadAiModels(): Promise<AiModelStatus> {
  const response = await fetch("/api/health", { headers: { accept: "application/json" }, cache: "no-store" });
  if (!response.ok) throw new Error("AIモデルの状態を取得できませんでした。");
  return (await response.json()) as AiModelStatus;
}

async function resetUsage(): Promise<UsageResetResult> {
  const response = await fetch("/admin/api/usage-reset", {
    method: "POST",
    headers: { accept: "application/json" },
    cache: "no-store",
  });
  if (!response.ok) throw new Error("本日の利用回数をリセットできませんでした。");
  return (await response.json()) as UsageResetResult;
}

function card(status: UsageStatus, message = ""): HTMLElement {
  const section = document.createElement("section");
  section.className = "admin-section";
  section.id = "admin-usage-limit";
  section.innerHTML = `
    <header><div><span>Usage limit</span><h2>利用制限</h2></div></header>
    <div class="admin-status-grid">
      <article class="admin-status-card">
        <span class="admin-status-dot ${status.enabled ? "is-ok" : "is-error"}" aria-hidden="true"></span>
        <div><h3>稼働状態</h3><strong>${status.enabled ? "有効" : "停止"}</strong><p>解析APIの利用制限</p></div>
      </article>
      <article class="admin-status-card">
        <span class="admin-status-dot is-ok" aria-hidden="true"></span>
        <div><h3>1人あたり</h3><strong>${escapeHtml(status.perClientLimit)}回 / 日</strong><p>同一利用元ごとの上限</p></div>
      </article>
      <article class="admin-status-card">
        <span class="admin-status-dot ${status.globalRemaining > 0 ? "is-ok" : "is-error"}" aria-hidden="true"></span>
        <div><h3>本日の全体利用</h3><strong>${escapeHtml(status.globalUsed)} / ${escapeHtml(status.globalLimit)}回</strong><p>残り ${escapeHtml(status.globalRemaining)}回</p></div>
      </article>
      <article class="admin-status-card">
        <span class="admin-status-dot is-ok" aria-hidden="true"></span>
        <div><h3>リセット</h3><strong>毎日 午前${escapeHtml(status.resetHourJst)}時</strong><p>日本時間</p></div>
      </article>
    </div>
    <div class="admin-usage-reset-zone">
      <div><strong>本日分を強制リセット</strong><p>アプリ側の全体・利用元カウントを0に戻します。履歴や設定には影響しません。CloudflareのAI無料枠は別管理のためリセットされません。</p></div>
      <button class="button button--danger button--wide admin-usage-reset" type="button">本日の利用回数をリセット</button>
      ${message ? `<p class="message message--notice admin-usage-reset-message">${escapeHtml(message)}</p>` : ""}
    </div>`;
  bindReset(section);
  return section;
}

function bindReset(section: HTMLElement): void {
  const button = section.querySelector<HTMLButtonElement>(".admin-usage-reset");
  if (!button) return;
  button.addEventListener("click", async () => {
    if (!window.confirm("本日の利用回数をリセットしますか？\n全体・利用元ごとの回数が0に戻ります。")) return;
    button.disabled = true;
    button.textContent = "リセットしています…";
    try {
      const reset = await resetUsage();
      const status = await loadUsageStatus();
      section.replaceWith(card(status, `本日分をリセットしました（リセット前：全体${reset.previousGlobalUsed}回）。`));
    } catch (error) {
      const message = error instanceof Error ? error.message : "本日の利用回数をリセットできませんでした。";
      const current = section.querySelector(".admin-usage-reset-message");
      const errorMessage = document.createElement("p");
      errorMessage.className = "message message--error admin-usage-reset-message";
      errorMessage.textContent = message;
      if (current) current.replaceWith(errorMessage);
      else button.insertAdjacentElement("afterend", errorMessage);
      button.disabled = false;
      button.textContent = "本日の利用回数をリセット";
    }
  });
}

function errorCard(message: string): HTMLElement {
  const section = document.createElement("section");
  section.className = "admin-section";
  section.id = "admin-usage-limit";
  section.innerHTML = `<header><div><span>Usage limit</span><h2>利用制限</h2></div></header><div class="message message--error">${escapeHtml(message)}</div>`;
  return section;
}

function deploymentSection(): HTMLElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLElement>(".admin-section")).find(
      (section) => section.querySelector("header span")?.textContent?.trim().toLowerCase() === "deployment",
    ) ?? null
  );
}

function setTextIfChanged(element: HTMLElement | null, value: string): void {
  if (element && element.textContent !== value) element.textContent = value;
}

function patchWorkersAiDescription(): void {
  for (const heading of document.querySelectorAll<HTMLElement>(".admin-status-card h3")) {
    if (heading.textContent?.trim() !== "Workers AI") continue;
    const paragraph = heading.closest(".admin-status-card")?.querySelector<HTMLElement>("p") ?? null;
    setTextIfChanged(paragraph, "画像解析・文章生成モデルのBindingを確認。推論は未実行");
  }
}

function applyAiModelDisplay(models: AiModelStatus): void {
  const section = deploymentSection();
  const details = section?.querySelector<HTMLElement>(".admin-details");
  if (!details) return;

  const rows = Array.from(details.querySelectorAll<HTMLElement>(":scope > div"));
  const visionRow = rows.find((row) => {
    const label = row.querySelector("dt")?.textContent?.trim();
    return label === "画像AI" || label === "画像解析AI";
  });
  if (!visionRow) return;

  const visionLabel = visionRow.querySelector<HTMLElement>("dt");
  const visionValue = visionRow.querySelector<HTMLElement>("dd");
  setTextIfChanged(visionLabel, "画像解析AI");
  if (models.visionModel) setTextIfChanged(visionValue, models.visionModel);

  let narrativeRow = rows.find((row) => row.querySelector("dt")?.textContent?.trim() === "文章生成AI");
  if (!narrativeRow) {
    narrativeRow = document.createElement("div");
    narrativeRow.innerHTML = "<dt>文章生成AI</dt><dd>確認中</dd>";
    visionRow.insertAdjacentElement("afterend", narrativeRow);
  }
  const narrativeValue = narrativeRow.querySelector<HTMLElement>("dd");
  if (models.narrativeModel) setTextIfChanged(narrativeValue, models.narrativeModel);

  patchWorkersAiDescription();
}

async function enhanceAiModelDisplay(): Promise<void> {
  if (window.location.pathname !== "/admin") return;
  try {
    aiModelsPromise ??= loadAiModels();
    applyAiModelDisplay(await aiModelsPromise);
  } catch {
    // 管理画面本体の表示は維持し、モデル状態の追加表示だけを静かに諦める。
  }
}

async function inject(): Promise<void> {
  if (window.location.pathname !== "/admin") return;

  try {
    const status = await loadUsageStatus();
    const existing = document.querySelector("#admin-usage-limit");
    if (existing) existing.replaceWith(card(status));
    else {
      const system = document.querySelector(".admin-section");
      system?.insertAdjacentElement("afterend", card(status));
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : "利用制限の状態を取得できませんでした。";
    const system = document.querySelector(".admin-section");
    if (!document.querySelector("#admin-usage-limit")) system?.insertAdjacentElement("afterend", errorCard(message));
  }
}

if (window.location.pathname === "/admin") {
  const observer = new MutationObserver(() => {
    if (!document.querySelector(".admin-page")) return;
    if (!document.querySelector("#admin-usage-limit")) void inject();
    void enhanceAiModelDisplay();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  void inject();
  void enhanceAiModelDisplay();
}
